import { formatPropertyAddress } from '../utils/propertyAddress';
import './MobileMapDisclosure.css';
import FleetPropertyDetailActions from './FleetPropertyDetailActions';
import {PropertyListPicker} from './PropertyLists';
import MapPlaceLayers from './MapPlaceLayers';
import {mapCoordinates} from '../utils/propertyCoordinates';
import PropertyLocationMap from './PropertyLocationMap';
import {addIllustratedBasemap} from '../services/illustratedBasemap';
import { propertyFetch } from '../services/idxPropertyClient';
import { rememberPropertyPhoto } from '../services/propertyThumbnails';
import { loadPropertyMedia, propertyMediaUrls } from '../services/propertyMedia';
import { getPropertyShareUrl } from '../utils/propertySharing';
import { usePropertySave } from './PropertySaveProvider';
import RobustSearchControls from './RobustSearchControls';
import useListSearchFilters from '../utils/useListSearchFilters';
import { searchFromQuery, searchQuery } from '../utils/propertySearchFilters';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { SITE_CONFIG } from '../config';
import PropertyThumbnail from './PropertyThumbnail';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './PropertyListView.css';

// LEGACY_FAVORITES_CACHE_2026
const LOCAL_FAVORITES_KEY = `propertyFavorites_${SITE_CONFIG.SITE_ID}`;
// Nine fills the three-column card grid evenly at every breakpoint.
const PAGE_SIZE = 9;
const PRICE_OPTIONS = [
  { value: '', label: 'Any' },
  { value: '250000', label: '$250k' },
  { value: '500000', label: '$500k' },
  { value: '750000', label: '$750k' },
  { value: '1000000', label: '$1M' },
  { value: '1500000', label: '$1.5M' },
  { value: '2000000', label: '$2M' },
  { value: '3000000', label: '$3M' }
];
const BEDROOM_OPTIONS = [
  { value: '', label: 'Any' },
  { value: '1', label: '1+' },
  { value: '2', label: '2+' },
  { value: '3', label: '3+' },
  { value: '4', label: '4+' },
  { value: '5', label: '5+' }
];
const compactPrice = value => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return String(value);
  return amount >= 1000000 ? `$${amount / 1000000}M` : `$${amount / 1000}k`;
};
const PropertyListView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useTheme();
  const initialListFilters = location.state?.searchFilters || searchFromQuery(location.search);

  const [properties, setProperties] = useState(() => location.state?.properties || []);
  const [listViewPage, setListViewPage] = useState(0);
  const [propertyDetail, setPropertyDetail] = useState(null);
  const [propertyDetailImages, setPropertyDetailImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const detailRequestRef = useRef(0);
  const [likedProperties, setLikedProperties] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(LOCAL_FAVORITES_KEY) || '[]');
      return new Set(Array.isArray(saved) ? saved : []);
    } catch (error) {
      return new Set();
    }
  });
  const { favorites, toggleFavorite } = usePropertySave();
  useEffect(() => { setLikedProperties?.(favorites); }, [favorites]);
  const [quickFilters, setQuickFilters] = useState(() => ({
    city: initialListFilters.city || '',
    minPrice: initialListFilters.minPrice || '',
    maxPrice: initialListFilters.maxPrice || '',
    minBedrooms: initialListFilters.minBedrooms || ''
  }));
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const listSearch = useListSearchFilters({
    initialFilters: location.state?.searchFilters || searchFromQuery(location.search),
    initialProperties: location.state?.properties,
    quickEdits: quickFilters,
    defaultState: SITE_CONFIG.SEARCH_STATES || SITE_CONFIG.STATE,
    siteId: SITE_CONFIG.SITE_ID, onResults: setProperties, onPageReset: setListViewPage, pageSize: PAGE_SIZE
  });
  // The hook merges each change into the criteria and re-runs the search itself.
  const updateQuickFilter = (key, value) => {
    setQuickFilters(previous => ({ ...previous, [key]: value }));
    setListViewPage(0);
  };
  const clearQuickFilters = () => {
    setQuickFilters({ city: '', minPrice: '', maxPrice: '', minBedrooms: '' });
    setListViewPage(0);
  };
  const activeFilterChips = [
    quickFilters.city.trim() && { key: 'city', label: quickFilters.city.trim() },
    quickFilters.minPrice && { key: 'minPrice', label: `${compactPrice(quickFilters.minPrice)}+` },
    quickFilters.maxPrice && { key: 'maxPrice', label: `Up to ${compactPrice(quickFilters.maxPrice)}` },
    quickFilters.minBedrooms && { key: 'minBedrooms', label: `${quickFilters.minBedrooms}+ beds` }
  ].filter(Boolean);
  const resultSummary = listSearch.loading
    ? 'Searching homes…'
    : `${properties.length} ${properties.length === 1 ? 'home' : 'homes'} loaded`;

  const [showLogin, setShowLogin] = useState(false);
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [expandedMap, setExpandedMap] = useState(null);
  const [placesExpandedMapInstance,setPlacesExpandedMapInstance]=useState(null);
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [enlargedImageIndex, setEnlargedImageIndex] = useState(0);
  const [showAiStaging, setShowAiStaging] = useState(false);
  const [stagingImage, setStagingImage] = useState(null);
  const [stagingLoading, setStagingLoading] = useState(false);

  const handleAiStaging = async (type) => {
    setStagingLoading(true);
    try {
      const promptResponse = await fetch(`https://tophamtechbeb.com/api/stage/v1/${type}-prompts/random`, {
        headers: { 'X-API-Key': 'treas-api-key-2025' }
      });
      if (!promptResponse.ok) throw new Error('Failed to get staging prompt');
      const promptData = await promptResponse.json();
      const formData = new FormData();
      formData.append('image_url', enlargedImage);
      formData.append('design_prompt', promptData.prompt);
      const stagingResponse = await fetch('https://tophamtechbeb.com/api/stage/v1/stage-room', {
        method: 'POST',
        headers: { 'X-API-Key': 'treas-api-key-2025' },
        body: formData
      });
      if (!stagingResponse.ok) throw new Error('Failed to stage room');
      const stagedBlob = await stagingResponse.blob();
      const imageUrl = URL.createObjectURL(stagedBlob);
      setStagingImage(imageUrl);
      setShowAiStaging(false);
    } catch (error) {
      console.error('Error staging room:', error);
      alert('Failed to stage room. Please try again.');
    } finally {
      setStagingLoading(false);
    }
  };

  const fetchPropertyDetail = async (propertyId, summaryData) => {
    const requestId = ++detailRequestRef.current;
    const cover = summaryData?.first_photo_url || summaryData?.image_url || summaryData?.photo_url;
    setPropertyDetail(summaryData || null);
    setPropertyDetailImages(cover ? [cover] : []);
    setImagesLoading(!cover);
    try {
      const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-data/${propertyId}`, {});
      if (requestId !== detailRequestRef.current) return;
      if (response.ok) {
        const data = await response.json();
        const property = data.properties[0];
        setPropertyDetail({
          ...property,
          created_at: property.created_at || summaryData?.created_at,
          updated_at: property.updated_at || summaryData?.updated_at,
          listing_contract_date: property.listing_contract_date || summaryData?.listing_contract_date
        });
        if (property.listing_key) {
          fetchAllPropertyImages(property.listing_key, requestId, cover);
        }
      } else {
        setImagesLoading(false);
      }
    } catch (error) {
      console.error('Error fetching property details:', error);
      if (requestId === detailRequestRef.current) setImagesLoading(false);
    }
  };

  const fetchAllPropertyImages = async (listingKey, requestId, cover) => {
    try {
      const images = propertyMediaUrls(await loadPropertyMedia(listingKey));
      if (requestId !== detailRequestRef.current) return;
      if (images.length > 0) {
          const firstThree = images.slice(0, 3);
          setPropertyDetailImages(firstThree);
          setImagesLoading(false);
          rememberPropertyPhoto(listingKey, images[0]);
          if (images.length > 3) {
            setTimeout(() => {
              if (requestId === detailRequestRef.current) setPropertyDetailImages(images);
            }, 500);
          }
      } else {
        setPropertyDetailImages(cover ? [cover] : []);
        setImagesLoading(false);
      }
    } catch (error) {
      console.error('Error fetching all property images:', error);
      setPropertyDetailImages([]);
      setImagesLoading(false);
    }
  };

  const closePropertyDetail = () => {
    detailRequestRef.current += 1;
    setPropertyDetail(null);
    setImagesLoading(false);
  };

  useEffect(() => {
    if (location.state?.properties) {
      setProperties(location.state.properties);
    }
  }, [location.state]);

  const user = location.state?.user;

  const getTileCoords = (lat, lon, zoom) => {
    const x = Math.floor((lon + 180) / 360 * Math.pow(2, zoom));
    const y = Math.floor((1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI) / 2 * Math.pow(2, zoom));
    return { x, y };
  };

  useEffect(() => {
    if (expandedMap && mapRef.current) {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
      }
      const map = L.map(mapRef.current,{minZoom:1,maxZoom:19}).setView([expandedMap.lat, expandedMap.lon], 15);
      addIllustratedBasemap(map);
      const houseIcon = L.divIcon({
        html: '🏠',
        className: 'house-marker',
        iconSize: [30, 30],
        iconAnchor: [15, 15]
      });
      L.marker([expandedMap.lat, expandedMap.lon], { icon: houseIcon }).addTo(map);
      leafletMapRef.current = map;
      setPlacesExpandedMapInstance(map);
    }
    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
        setPlacesExpandedMapInstance(null);
      }
    };
  }, [expandedMap]);

  return (
    <div className={`property-list-view ${isDarkMode ? 'dark-mode' : ''}`}>
      <header className="list-view-header">
        <div className="list-view-actions">
          <button
            type="button"
            className="list-view-back-btn"
            onClick={() => navigate('/search')}
            aria-label="Back to property search filters"
          >
            ← Back to Search
          </button>
          <button
            type="button"
            className="back-to-map-btn"
            onClick={() => {if(location.state?.isSavedHomes && location.state?.savedMap){sessionStorage.setItem('globeMapData',JSON.stringify(location.state.savedMap));navigate('/search?saved=true');}else{const params=searchQuery(listSearch.filters);params.set('view','map');navigate(`/search?${params}`);}}}
            aria-label="Show these results on the map"
          >
            🗺 Map View
          </button>
          <span className="list-view-count" aria-live="polite">{resultSummary}</span>
        </div>

        <div className={`quick-filter-bar ${mobileFiltersOpen ? 'mobile-open' : 'mobile-collapsed'}`}>
          <div className="quick-filter-mobile-summary">
            <span className="quick-filter-mobile-count">{resultSummary}</span>
            <button
              type="button"
              className="quick-filter-mobile-toggle"
              onClick={() => setMobileFiltersOpen(open => !open)}
              aria-expanded={mobileFiltersOpen}
              aria-controls="quick-filter-panel"
            >
              Filters
              {activeFilterChips.length > 0 && <span className="quick-filter-count-badge">{activeFilterChips.length}</span>}
              <span className="quick-filter-chevron" aria-hidden="true">{mobileFiltersOpen ? '▴' : '▾'}</span>
            </button>
          </div>

          {!mobileFiltersOpen && activeFilterChips.length > 0 && (
            <div className="quick-filter-chips" aria-label="Active filters">
              {activeFilterChips.map(filter => (
                <button
                  key={filter.key}
                  type="button"
                  className="quick-filter-chip"
                  onClick={() => updateQuickFilter(filter.key, '')}
                  aria-label={`Remove ${filter.label} filter`}
                >
                  {filter.label} <span aria-hidden="true">×</span>
                </button>
              ))}
            </div>
          )}

          <div id="quick-filter-panel" className="quick-filter-panel">
            <div className="quick-filter-main">
              <label className="quick-filter-field quick-filter-city">
                <span>City or Area</span>
                <input
                  type="text"
                  value={quickFilters.city}
                  onChange={(e) => updateQuickFilter('city', e.target.value)}
                  placeholder="Westminster, Garden Grove…"
                />
              </label>
              <label className="quick-filter-field">
                <span>Min Price</span>
                <select value={quickFilters.minPrice} onChange={(e) => updateQuickFilter('minPrice', e.target.value)}>
                  {PRICE_OPTIONS.map(option => <option key={option.value || 'min-any'} value={option.value}>{option.label}</option>)}
                </select>
              </label>
              <label className="quick-filter-field">
                <span>Max Price</span>
                <select value={quickFilters.maxPrice} onChange={(e) => updateQuickFilter('maxPrice', e.target.value)}>
                  {PRICE_OPTIONS.map(option => <option key={option.value || 'max-any'} value={option.value}>{option.label}</option>)}
                </select>
              </label>
              <label className="quick-filter-field">
                <span>Beds</span>
                <select value={quickFilters.minBedrooms} onChange={(e) => updateQuickFilter('minBedrooms', e.target.value)}>
                  {BEDROOM_OPTIONS.map(option => <option key={option.value || 'beds-any'} value={option.value}>{option.label}</option>)}
                </select>
              </label>
              {activeFilterChips.length > 0 && (
                <button type="button" className="quick-filter-clear" onClick={clearQuickFilters}>
                  Clear
                </button>
              )}
            </div>

            <RobustSearchControls
              filters={listSearch.filters}
              onChange={listSearch.setFilters}
              scope={{state:listSearch.filters.state || SITE_CONFIG.SEARCH_STATES || SITE_CONFIG.STATE, propertyType:listSearch.filters.propertyType || 'Residential'}}
            />

            <button
              type="button"
              className="quick-filter-mobile-done"
              onClick={() => setMobileFiltersOpen(false)}
              disabled={listSearch.loading}
            >
              {listSearch.loading ? 'Searching homes…' : 'View homes'}
            </button>
          </div>
        </div>
      </header>

      <div className="list-view-container">
        {listSearch.error && <div className="list-view-state list-view-error" role="alert">{listSearch.error}</div>}

        {listSearch.loading && properties.length === 0 && (
          <div className="list-view-state" role="status">Loading matching homes…</div>
        )}

        {!listSearch.loading && !listSearch.error && properties.length === 0 && (
          <div className="list-view-state">No homes match these filters.</div>
        )}

        {properties.slice(listViewPage * PAGE_SIZE, (listViewPage + 1) * PAGE_SIZE).map((property, index) => {
          const address = formatPropertyAddress(property);
          const attribution = [property.list_agent_full_name, property.list_office_name].filter(Boolean).join(' | ');
          return (
            <article
              key={index}
              className="list-view-card"
              onClick={() => fetchPropertyDetail(property.id, property)}
            >
              <div className="list-view-hero">
                <PropertyThumbnail
                  listingKey={property.listing_key}
                  src={property.first_photo_url || property.image_url || property.photo_url}
                  alt={`Property at ${property.address || property.street_address || address}`}
                  className="list-view-image"
                />
                <div className="list-view-overlay">
                  <div className="list-view-price">
                    ${property.current_price?.toLocaleString() || property.price}
                  </div>
                  <div className="list-view-address">{address}</div>
                  <div className="list-view-meta">
                    {property.bedrooms_total && <span>🛏️ {property.bedrooms_total} bed</span>}
                    {property.property_type && <span>{property.property_type}</span>}
                  </div>
                </div>
                {property.latitude && property.longitude && (
                  <div
                    className="list-view-map"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedMap({lat: property.latitude, lon: property.longitude, address: property.address});
                    }}
                  >
                    <img
                      src={`https://tile.openstreetmap.org/15/${getTileCoords(property.latitude, property.longitude, 15).x}/${getTileCoords(property.latitude, property.longitude, 15).y}.png`}
                      alt={`Map location for ${property.address || property.street_address}`}
                      loading="lazy"
                    />
                    <div className="list-view-house-icon">🏠</div>
                  </div>
                )}
              </div>
              {attribution && (
                <div className="list-view-attribution">
                  <span>Listed By: {attribution}</span>
                  {property.attribution_contact && <span>{property.attribution_contact}</span>}
                </div>
              )}
            </article>
          );
        })}

        <div className="pagination-controls">
          {listSearch.restartSearch && (
            <button type="button" className="pagination-btn" onClick={()=>listSearch.restart()}>Restart search</button>
          )}
          {properties.length > 0 && listViewPage > 0 && (
            <button
              disabled={listSearch.loading} onClick={() => setListViewPage(listViewPage - 1)}
              className="pagination-btn"
            >
              ← Previous
            </button>
          )}
          {properties.length > 0 && (
            <span className="pagination-info">Page {listViewPage + 1}</span>
          )}
          {properties.length > 0 && (listSearch.hasMore || (listViewPage + 1) * PAGE_SIZE < properties.length) && (
            <button
              disabled={listSearch.loading} onClick={() => listSearch.nextPage(listViewPage)}
              className="pagination-btn"
            >
              {listSearch.loading ? 'Loading…' : 'Next →'}
            </button>
          )}
        </div>
      </div>

      <div className="list-view-mls-footer mobile-map-disclosure">
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/crmlslogo.png" alt="CRMLS" />
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/equalhousinglogo.png" alt="Equal Housing Opportunity" />
        <span>Based on information from California Regional Multiple Listing Service, Inc. and/or other sources. All data, including all measurements and calculations of area, is obtained from various sources and has not been, and will not be, verified by broker or MLS. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information. Any amount of compensation or commission stated is made only to participants of CRMLS or an MLS that has entered into a datashare agreement with CRMLS.</span>
      </div>

      {propertyDetail && (
        <div className="modal-overlay" onClick={closePropertyDetail}>
          <><FleetPropertyDetailActions home={propertyDetail} user={user} /><div className="property-detail-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal" onClick={closePropertyDetail}>×</button>
            <button
              className="link-button"
              onClick={() => {
                const url = getPropertyShareUrl(propertyDetail, SITE_CONFIG);
                navigator.clipboard.writeText(url).catch(err => console.error('Failed to copy:', err));
              }}
              aria-label="Copy property link to clipboard"
            >
              🔗
            </button>
            <button
              className={`heart-button ${likedProperties.has(propertyDetail.listing_key) ? 'liked' : ''}`}
              onClick={(e) => { e.stopPropagation(); toggleFavorite(propertyDetail); }}
            >
              {likedProperties.has(propertyDetail.listing_key) ? '💖' : '❤️'}
            </button>
            <div className="property-detail-content">
              <div className="property-header">
<div className={`property-overview-grid${mapCoordinates(propertyDetail) ? '' : ' property-overview-grid--no-map'}`}><div className="property-overview-summary">
                {(propertyDetail.list_agent_full_name || propertyDetail.list_office_name) && (
                  <div style={{fontSize: '13px', color: '#555', marginBottom: '6px', textAlign: 'left'}}>
                    <div>Listed By: {[propertyDetail.list_agent_full_name, propertyDetail.list_office_name].filter(Boolean).join(' | ')}</div>
                    {propertyDetail.attribution_contact && <div>{propertyDetail.attribution_contact}</div>}
                  </div>
                )}
                <h2>${propertyDetail.current_price?.toLocaleString()}</h2>
                <p style={{fontWeight: 'normal', fontSize: '15px', color: '#555', textShadow: 'none'}}>{formatPropertyAddress(propertyDetail)}</p>

                </div><PropertyLocationMap property={propertyDetail} /></div>
<div className="property-images">
                  {imagesLoading ? (
                    <div style={{textAlign: 'center', padding: '20px', color: '#007bff', fontSize: '14px'}}>
                      <div style={{display: 'inline-block', marginRight: '8px', animation: 'spin 1s linear infinite', fontSize: '16px'}}>⟳</div>
                      Loading images...
                    </div>
                  ) : propertyDetailImages.length > 0 ? (
                    propertyDetailImages.map((imageUrl, index) => (
                      <div key={index} className="property-detail-image" onClick={() => { setEnlargedImage(imageUrl); setEnlargedImageIndex(index); }}>
                        <img src={imageUrl} alt={`Property ${index + 1}`} onLoad={() => rememberPropertyPhoto(propertyDetail?.listing_key, imageUrl)} />
                      </div>
                    ))
                  ) : (
                    <div style={{textAlign: 'center', padding: '20px', color: '#666', fontSize: '14px'}}>
                      No images available
                    </div>
                  )}
                </div>

                {propertyDetail.features?.public_remarks && (
                  <div className="property-description">
                    <h3>Property Description</h3>
                    <p>{propertyDetail.features.public_remarks}</p>
                  </div>
                )}
              </div>

              <div className="property-columns">
                <div className="property-column">
                  <h3>Property Details</h3>
                  <p><strong>Property Type:</strong> {propertyDetail.property_type}</p>
                  {propertyDetail.property_sub_type && <p><strong>Property Sub Type:</strong> {propertyDetail.property_sub_type}</p>}
                  <p><strong>Listing Key:</strong> {propertyDetail.listing_key}</p>
                  <p><strong>Current Price:</strong> ${propertyDetail.current_price?.toLocaleString()}</p>
                  <p><strong>List Price:</strong> ${propertyDetail.list_price?.toLocaleString()}</p>
                  {propertyDetail.original_list_price && <p><strong>Original List Price:</strong> ${propertyDetail.original_list_price?.toLocaleString()}</p>}
                  <p><strong>Living Area:</strong> {propertyDetail.living_area} sq ft</p>
                  <p><strong>Building Area:</strong> {propertyDetail.building_area_total} sq ft</p>
                  <p><strong>Year Built:</strong> {propertyDetail.year_built}</p>
                  {propertyDetail.stories && <p><strong>Stories:</strong> {propertyDetail.stories}</p>}
                  {propertyDetail.listing_contract_date && (
                    <>
                      <p><strong>Listed Date:</strong> {new Date(propertyDetail.listing_contract_date).toLocaleDateString()}</p>
                      <p><strong>Days on Market:</strong> {Math.floor((Date.now() - new Date(propertyDetail.listing_contract_date)) / 86400000)}</p>
                    </>
                  )}
                  {(propertyDetail.updated_at || propertyDetail.created_at) && (
                    <p><strong>Last Updated:</strong> {new Date(propertyDetail.updated_at || propertyDetail.created_at).toLocaleString()}</p>
                  )}

                  {propertyDetail.land && (
                    <div>
                      <h3>Land Details</h3>
                      {propertyDetail.land.lot_size_acres && <p><strong>Lot Size:</strong> {propertyDetail.land.lot_size_acres} acres ({propertyDetail.land.lot_size_square_feet?.toLocaleString()} sq ft)</p>}
                      {propertyDetail.land.lot_features && <p><strong>Lot Features:</strong> {propertyDetail.land.lot_features}</p>}
                      {propertyDetail.land.zoning && <p><strong>Zoning:</strong> {propertyDetail.land.zoning}</p>}
                    </div>
                  )}

                  <h3>Rooms & Bathrooms</h3>
                  {propertyDetail.bedrooms_total != null && <p><strong>Bedrooms:</strong> {propertyDetail.bedrooms_total}</p>}
                  {propertyDetail.bathrooms_full != null && <p><strong>Full Bathrooms:</strong> {propertyDetail.bathrooms_full}</p>}
                  {propertyDetail.bathrooms_half != null && <p><strong>Half Bathrooms:</strong> {propertyDetail.bathrooms_half}</p>}
                  {propertyDetail.fireplaces_total != null && <p><strong>Fireplaces:</strong> {propertyDetail.fireplaces_total}</p>}

                  {propertyDetail.schools && (
                    <div>
                      <h3>Schools</h3>
                      <p><strong>Elementary:</strong> {propertyDetail.schools.elementary_school}</p>
                      <p><strong>Middle:</strong> {propertyDetail.schools.middle_school}</p>
                      <p><strong>High:</strong> {propertyDetail.schools.high_school}</p>
                    </div>
                  )}
                </div>

                <div className="property-column">
                  <h3>Features & Amenities</h3>
                  {propertyDetail.features?.garage_spaces != null && <p><strong>Garage:</strong> {propertyDetail.features.garage_spaces} spaces</p>}
                  {propertyDetail.features?.heating && <p><strong>Heating:</strong> {propertyDetail.features.heating}</p>}
                  {propertyDetail.features?.cooling && <p><strong>Cooling:</strong> {propertyDetail.features.cooling}</p>}
                  {propertyDetail.features?.water_source && <p><strong>Water Source:</strong> {propertyDetail.features.water_source}</p>}
                  {propertyDetail.foundation_details && <p><strong>Foundation:</strong> {propertyDetail.foundation_details}</p>}
                  {propertyDetail.roof && <p><strong>Roof:</strong> {propertyDetail.roof}</p>}
                  {propertyDetail.sewer && <p><strong>Sewer:</strong> {propertyDetail.sewer}</p>}

                  {propertyDetail.features?.appliances && (
                    <div>
                      <h3>Appliances</h3>
                      <p className="feature-list">{propertyDetail.features.appliances.split(',').join(', ')}</p>
                    </div>
                  )}

                  {propertyDetail.features?.interior_features && (
                    <div>
                      <h3>Interior Features</h3>
                      <p className="feature-list">{propertyDetail.features.interior_features.split(',').join(', ')}</p>
                    </div>
                  )}

                  {propertyDetail.features?.exterior_features && (
                    <div>
                      <h3>Exterior Features</h3>
                      <p className="feature-list">{propertyDetail.features.exterior_features.split(',').join(', ')}</p>
                    </div>
                  )}

                  {propertyDetail.features?.fencing && (
                    <div>
                      <h3>Fencing</h3>
                      <p>{propertyDetail.features.fencing}</p>
                    </div>
                  )}

                  {propertyDetail.association?.association_name && (
                    <div>
                      <h3>HOA Information</h3>
                      <p><strong>Association:</strong> {propertyDetail.association.association_name}</p>
                      {propertyDetail.association.association_fee && <p><strong>HOA Fee:</strong> ${propertyDetail.association.association_fee} {propertyDetail.association.fee_frequency}</p>}
                    </div>
                  )}

                  {propertyDetail.features?.lots_features && (
                    <div>
                      <h3>Lot Features</h3>
                      <p className="feature-list">{propertyDetail.features.lots_features.split(',').join(', ')}</p>
                    </div>
                  )}

                  {propertyDetail.features?.patio_and_porch_features && (
                    <div>
                      <h3>Patio & Porch</h3>
                      <p className="feature-list">{propertyDetail.features.patio_and_porch_features.split(',').join(', ')}</p>
                    </div>
                  )}
                </div>
              </div>
              <div style={{borderTop: '1px solid #ddd', marginTop: '20px', paddingTop: '16px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap'}}>
                <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/crmlslogo.png" alt="CRMLS" style={{height: '32px', objectFit: 'contain'}} />
                <div style={{fontSize: '12px', color: '#555', flex: 1}}>
                  <div style={{marginBottom: '4px'}}>Listed By: {[propertyDetail.list_agent_full_name, propertyDetail.list_office_name].filter(Boolean).join(' | ')}{propertyDetail.attribution_contact ? ` — ${propertyDetail.attribution_contact}` : ''}</div>
                  <div style={{fontSize: '10px', color: '#666'}}>Based on information from California Regional Multiple Listing Service, Inc. as of {propertyDetail.created_at ? new Date(propertyDetail.created_at).toLocaleDateString() : new Date().toLocaleDateString()} and/or other sources. All data, including all measurements and calculations of area, is obtained from various sources and has not been, and will not be, verified by broker or MLS. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information. Any amount of compensation or commission stated is made only to participants of CRMLS or an MLS that has entered into a datashare agreement with CRMLS.</div>
                </div>
              </div>
            </div>
          </div></>
        </div>
      )}

      {showLogin && (
        <div className="modal-overlay" onClick={() => setShowLogin(false)}>
          <div className="property-login-form" onClick={(e) => e.stopPropagation()}>
            <h3>Sign In to Save Property</h3>
            <form className="chat-form" onSubmit={async (e) => {
              e.preventDefault();
              try {
                const response = await fetch('https://tophamtechbe.com/api/treas/auth/signin', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ username: loginUsername, password: loginPassword, siteId: SITE_CONFIG.SITE_ID })
                });
                if (response.ok) {
                  const userData = await response.json();
                  localStorage.setItem('user', JSON.stringify(userData));
                  setShowLogin(false);
                  window.location.reload();
                } else {
                  alert('Login failed. Please check your credentials.');
                }
              } catch (error) {
                alert('Login failed. Please try again.');
              }
            }}>
              <input type="text" placeholder="Username or Email" value={loginUsername} onChange={(e) => setLoginUsername(e.target.value)} className="form-input" required />
              <input type="password" placeholder="Password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} className="form-input" required />
              <button type="submit" className="form-submit">Sign In</button>
              <button type="button" className="form-submit" onClick={() => setShowLogin(false)} style={{background: '#6c757d'}}>Cancel</button>
            </form>
          </div>
        </div>
      )}

      {enlargedImage && (
        <div className="enlarged-image-modal" onClick={() => { setEnlargedImage(null); setStagingImage(null); }}>
          <div className="enlarged-image-container" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal" onClick={() => { setEnlargedImage(null); setStagingImage(null); }}>×</button>
            <button className="ai-staging-button" onClick={() => setShowAiStaging(true)}>🤖</button>
            <img src={stagingImage || enlargedImage} alt="Enlarged Property" className="enlarged-image" />
            {stagingImage && (
              <button className="reset-staging-button" onClick={() => setStagingImage(null)}>Original</button>
            )}
            {propertyDetailImages.length > 1 && (
              <>
                <button
                  className="image-nav-arrow prev-arrow"
                  onClick={() => {
                    const prevIndex = enlargedImageIndex > 0 ? enlargedImageIndex - 1 : propertyDetailImages.length - 1;
                    setEnlargedImageIndex(prevIndex);
                    setEnlargedImage(propertyDetailImages[prevIndex]);
                  }}
                >
                  ‹
                </button>
                <button
                  className="image-nav-arrow next-arrow"
                  onClick={() => {
                    const nextIndex = enlargedImageIndex < propertyDetailImages.length - 1 ? enlargedImageIndex + 1 : 0;
                    setEnlargedImageIndex(nextIndex);
                    setEnlargedImage(propertyDetailImages[nextIndex]);
                  }}
                >
                  ›
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {showAiStaging && (
        <div className="ai-staging-modal" onClick={() => setShowAiStaging(false)}>
          <div className="ai-staging-content" onClick={(e) => e.stopPropagation()}>
            <h3>AI Room Staging</h3>
            <p>Select room type to generate AI staging:</p>
            <div className="staging-buttons">
              <button className="staging-type-button" onClick={() => handleAiStaging('bedroom')} disabled={stagingLoading}>🛌️ Bedroom</button>
              <button className="staging-type-button" onClick={() => handleAiStaging('office')} disabled={stagingLoading}>💼 Office</button>
              <button className="staging-type-button" onClick={() => handleAiStaging('landscape')} disabled={stagingLoading}>🌳 Landscape</button>
              <button className="staging-type-button" onClick={() => handleAiStaging('room')} disabled={stagingLoading}>🏠 Room</button>
            </div>
            {stagingLoading && (
              <div className="staging-loading">
                <div className="loading-spinner"></div>
                <p>Generating AI staging...</p>
              </div>
            )}
            <button className="close-staging-modal" onClick={() => setShowAiStaging(false)}>Cancel</button>
          </div>
        </div>
      )}

      {expandedMap && (
        <div className="modal-overlay" onClick={() => setExpandedMap(null)}>
          <div className="expanded-map" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal" onClick={() => setExpandedMap(null)}>×</button>
            <div ref={mapRef} className="leaflet-map"></div>
              <MapPlaceLayers map={placesExpandedMapInstance} />
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyListView;
